import React from "react";
import useWindowSize from "../../../../hooks/useWindowSize";
import { func } from "../../../../utils";

const KnockoutTable = ({ knockoutData, roundOf16, categoryKey }) => {
  // Tiga bentuk bagan yang beda, jangan dicampur:
  //   roundOf16   -> SOC 2025, 16 tim (round of 16 -> 2x semifinal -> final)
  //   SMP & Kampus 2026 -> 4 tim, 2 teratas dari 2 grup series (semifinal -> final)
  //   sisanya     -> 8 tim (quarterfinal -> semifinal -> final)
  // categoryKey dikirim dari pemanggil karena knockoutData.category ikut hilang
  // waktu datanya kosong, dan tanpa itu SMP/Kampus ke-render pakai bagan SMA.
  const isRoundOf16 = !!roundOf16;
  const category = categoryKey || knockoutData?.category;
  const isFourTeamBracket =
    !isRoundOf16 && (category === "futsalSMP" || category === "futsalKampus");
  const isSMP = isRoundOf16;

  const match = knockoutData;

  const dataRoundOf16 = match?.rounds
    ? match?.rounds?.flatMap((round) => round.filter((match) => match.knockout_round === "top_16"))
    : [];

  const dataQuarterFinals = match?.rounds
    ? match?.rounds?.flatMap((round) =>
        round.filter((match) => match.knockout_round === "quarterfinal"),
      )
    : [];

  const dataSemiFinals = match?.rounds
    ? match?.rounds?.flatMap((round) =>
        round.filter((match) => match.knockout_round === "semifinal"),
      )
    : [];

  const dataFinals = match?.rounds
    ? match?.rounds?.flatMap((round) => round.filter((match) => match.knockout_round === "final"))
    : [];

  const dataThirdPlace = match?.rounds
    ? match?.rounds?.flatMap((round) =>
        round.filter((match) => match.knockout_round === "third_place"),
      )
    : [];

  const EmptyTeamCapsule = ({ text, hasBack = false, hasFront = true }) => {
    return (
      <div className="flex">
        {hasBack && <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>}
        <div
          className={`flex items-center justify-between w-[172px] md:w-[196px] h-[42px] bg-[#006E8D] rounded-full`}
        >
          <span
            className={`pl-4 capitalize pr-0 py-2 text-[14px]/[15px] text-white block whitespace-normal overflow-wrap break-word`}
          >
            {text}
          </span>
        </div>
        {hasFront && <div className="relative self-center w-[14px] h-[2px] bg-[#23556d]"></div>}
      </div>
    );
  };

  const TeamCapsule = ({ club, secondClub, hasBack = false, hasFront = true }) => {
    const hasPlayed = club?.winner || secondClub?.winner;
    const isScored =
      club?.score === 0 &&
      secondClub?.score === 0 &&
      club.penalty_score === 0 &&
      secondClub.penalty_score === 0;

    return (
      <div className="flex">
        {hasBack && <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>}
        <div
          className={`flex items-center justify-between w-[172px] md:w-[196px] h-[42px] ${
            !hasPlayed ? "bg-[#006E8D]" : club?.winner ? "bg-[#e0f6fe]" : "bg-[#4593b4]"
          } rounded-full`}
        >
          <span
            className={`font-bold pl-4 pr-0 py-2 text-[13px]/[15px] md:text-[14px]/[15px] ${
              !hasPlayed ? "text-white" : club?.winner ? "text-[#222d3c]" : "text-[#d5e9f0]"
            } block whitespace-normal overflow-wrap break-word capitalize`}
          >
            {club?.name}
          </span>

          {club?.score !== undefined && (
            <div
              className={`relative h-[100%] w-[49px] text-center font-bold flex items-center justify-center ${
                !hasPlayed ? "bg-white" : club?.winner ? "bg-[#222d3c]" : "bg-[#d5e9f0]"
              } ${
                !hasPlayed ? "text-[#00BCF1]" : club?.winner ? "text-white" : "text-[#6c7480]"
              } rounded-full flex-shrink-0`}
            >
              {club?.winner_penalty ? (
                <img
                  src={func.getStaticImg(`icon/notes-red.svg`)}
                  alt="notes"
                  className="absolute right-0 top-0"
                />
              ) : null}
              {secondClub?.name ? (
                <>
                  <span>{secondClub.score !== undefined ? club?.score : "-"}</span>
                  {hasPlayed &&
                    club?.score === secondClub?.score &&
                    club?.penalty_score !== secondClub?.penalty_score && (
                      <span className="ml-1">{`(${club?.penalty_score})`}</span>
                    )}
                </>
              ) : (
                <span>{"-"}</span>
              )}
            </div>
          )}
        </div>
        {hasFront && <div className="relative self-center w-[14px] h-[2px] bg-[#23556d]"></div>}
      </div>
    );
  };

  const RoundOf16 = ({ dataRoundOf16 }) => {
    return (
      <div className="flex flex-col gap-14">
        {dataRoundOf16[0] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[0]?.club1 || dataRoundOf16[0]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[0]?.club1 || dataRoundOf16[0]?.home_club}
                  secondClub={dataRoundOf16[0]?.club2 || dataRoundOf16[0]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 1"} />
              )}
              {dataRoundOf16[0]?.club2 || dataRoundOf16[0]?.away_club ? (
                <TeamCapsule
                  club={dataRoundOf16[0]?.club2 || dataRoundOf16[0]?.away_club}
                  secondClub={dataRoundOf16[0]?.club1 || dataRoundOf16[0]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 2"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 1"} />
              <EmptyTeamCapsule text={"Team 2"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataRoundOf16[1] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[1]?.club1 || dataRoundOf16[1]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[1]?.club1 || dataRoundOf16[1]?.home_club}
                  secondClub={dataRoundOf16[1]?.club2 || dataRoundOf16[1]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 3"} />
              )}
              {dataRoundOf16[1]?.club2 || dataRoundOf16[1]?.away_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[1]?.club2 || dataRoundOf16[1]?.away_club}
                  secondClub={dataRoundOf16[1]?.club1 || dataRoundOf16[1]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 4"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 3"} />
              <EmptyTeamCapsule text={"Team 4"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataRoundOf16[2] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[2]?.club1 || dataRoundOf16[2]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[2]?.club1 || dataRoundOf16[2]?.home_club}
                  secondClub={dataRoundOf16[2]?.club2 || dataRoundOf16[2]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 5"} />
              )}
              {dataRoundOf16[2]?.club2 || dataRoundOf16[2]?.away_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[2]?.club2 || dataRoundOf16[2]?.away_club}
                  secondClub={dataRoundOf16[2]?.club1 || dataRoundOf16[2]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 6"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 5"} />
              <EmptyTeamCapsule text={"Team 6"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataRoundOf16[3] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[3]?.club1 || dataRoundOf16[3]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[3]?.club1 || dataRoundOf16[3]?.home_club}
                  secondClub={dataRoundOf16[3]?.club2 || dataRoundOf16[3]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 7"} />
              )}
              {dataRoundOf16[3]?.club2 || dataRoundOf16[3]?.away_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[3]?.club2 || dataRoundOf16[3]?.away_club}
                  secondClub={dataRoundOf16[3]?.club1 || dataRoundOf16[3]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 8"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 7"} />
              <EmptyTeamCapsule text={"Team 8"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataRoundOf16[4] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[4]?.club1 || dataRoundOf16[4]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[4]?.club1 || dataRoundOf16[4]?.home_club}
                  secondClub={dataRoundOf16[4]?.club2 || dataRoundOf16[4]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 9"} />
              )}
              {dataRoundOf16[4]?.club2 || dataRoundOf16[4]?.away_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[4]?.club2 || dataRoundOf16[4]?.away_club}
                  secondClub={dataRoundOf16[4]?.club1 || dataRoundOf16[4]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 10"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 9"} />
              <EmptyTeamCapsule text={"Team 10"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataRoundOf16[5] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[5]?.club1 || dataRoundOf16[5]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[5]?.club1 || dataRoundOf16[5]?.home_club}
                  secondClub={dataRoundOf16[5]?.club2 || dataRoundOf16[5]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 11"} />
              )}
              {dataRoundOf16[5]?.club2 || dataRoundOf16[5]?.away_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[5]?.club2 || dataRoundOf16[5]?.away_club}
                  secondClub={dataRoundOf16[5]?.club1 || dataRoundOf16[5]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 12"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 11"} />
              <EmptyTeamCapsule text={"Team 12"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataRoundOf16[6] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[6]?.club1 || dataRoundOf16[6]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[6]?.club1 || dataRoundOf16[6]?.home_club}
                  secondClub={dataRoundOf16[6]?.club2 || dataRoundOf16[6]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 13"} />
              )}
              {dataRoundOf16[6]?.club2 || dataRoundOf16[6]?.away_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[6]?.club2 || dataRoundOf16[6]?.away_club}
                  secondClub={dataRoundOf16[6]?.club1 || dataRoundOf16[6]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 14"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 13"} />
              <EmptyTeamCapsule text={"Team 14"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataRoundOf16[7] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataRoundOf16[7]?.club1 || dataRoundOf16[7]?.home_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[7]?.club1 || dataRoundOf16[7]?.home_club}
                  secondClub={dataRoundOf16[7]?.club2 || dataRoundOf16[7]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 15"} />
              )}
              {dataRoundOf16[7]?.club2 || dataRoundOf16[7]?.away_club?.id ? (
                <TeamCapsule
                  club={dataRoundOf16[7]?.club2 || dataRoundOf16[7]?.away_club}
                  secondClub={dataRoundOf16[7]?.club1 || dataRoundOf16[7]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={"Team 16"} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Team 15"} />
              <EmptyTeamCapsule text={"Team 16"} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}
      </div>
    );
  };

  const QuarterFinals = ({ dataQuarterFinals, isSMP }) => {
    // Seeding beda per kategori (cuma juara & runner-up yang lolos):
    //  SMA    = 4 grup A-D            -> 1A/2B, 1B/2A, 1C/2D, 1D/2C
    //  lainnya = urut: 1v2, 3v4, 5v6, 7v8 (SMP & Kuliah ga lewat quarterfinal)
    const seeds =
      category === "futsalSMA"
        ? [
            ["Posisi 1A", "Posisi 2B"],
            ["Posisi 1B", "Posisi 2A"],
            ["Posisi 1C", "Posisi 2D"],
            ["Posisi 1D", "Posisi 2C"],
          ]
        : [
            ["Tim 1", "Tim 2"],
            ["Tim 3", "Tim 4"],
            ["Tim 5", "Tim 6"],
            ["Tim 7", "Tim 8"],
          ];
    return (
      <div className="flex flex-col gap-14">
        {dataQuarterFinals[0] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataQuarterFinals[0]?.club1 || dataQuarterFinals[0]?.home_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[0]?.club1 || dataQuarterFinals[0]?.home_club}
                  secondClub={dataQuarterFinals[0]?.club2 || dataQuarterFinals[0]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[0][0]} />
              )}
              {dataQuarterFinals[0]?.club2 || dataQuarterFinals[0]?.away_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[0]?.club2 || dataQuarterFinals[0]?.away_club}
                  secondClub={dataQuarterFinals[0]?.club1 || dataQuarterFinals[0]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[0][1]} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={seeds[0][0]} />
              <EmptyTeamCapsule text={seeds[0][1]} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataQuarterFinals[1] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataQuarterFinals[1]?.club1 || dataQuarterFinals[1]?.home_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[1]?.club1 || dataQuarterFinals[1]?.home_club}
                  secondClub={dataQuarterFinals[1]?.club2 || dataQuarterFinals[1]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[1][0]} />
              )}
              {dataQuarterFinals[1]?.club2 || dataQuarterFinals[1]?.away_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[1]?.club2 || dataQuarterFinals[1]?.away_club}
                  secondClub={dataQuarterFinals[1]?.club1 || dataQuarterFinals[1]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[1][1]} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={seeds[1][0]} />
              <EmptyTeamCapsule text={seeds[1][1]} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataQuarterFinals[2] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataQuarterFinals[2]?.club1 || dataQuarterFinals[2]?.home_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[2]?.club1 || dataQuarterFinals[2]?.home_club}
                  secondClub={dataQuarterFinals[2]?.club2 || dataQuarterFinals[2]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[2][0]} />
              )}
              {dataQuarterFinals[2]?.club2 || dataQuarterFinals[2]?.away_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[2]?.club2 || dataQuarterFinals[2]?.away_club}
                  secondClub={dataQuarterFinals[2]?.club1 || dataQuarterFinals[2]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[2][1]} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={seeds[2][0]} />
              <EmptyTeamCapsule text={seeds[2][1]} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}

        {dataQuarterFinals[3] ? (
          <div className="flex">
            <div className="flex flex-col gap-6">
              {dataQuarterFinals[3]?.club1 || dataQuarterFinals[3]?.home_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[3]?.club1 || dataQuarterFinals[3]?.home_club}
                  secondClub={dataQuarterFinals[3]?.club2 || dataQuarterFinals[3]?.away_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[3][0]} />
              )}
              {dataQuarterFinals[3]?.club2 || dataQuarterFinals[3]?.away_club ? (
                <TeamCapsule
                  club={dataQuarterFinals[3]?.club2 || dataQuarterFinals[3]?.away_club}
                  secondClub={dataQuarterFinals[3]?.club1 || dataQuarterFinals[3]?.home_club}
                />
              ) : (
                <EmptyTeamCapsule text={seeds[3][1]} />
              )}
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        ) : (
          <div className="flex">
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={seeds[3][0]} />
              <EmptyTeamCapsule text={seeds[3][1]} />
            </div>
            <div className={`relative self-center h-[59px] w-1 bg-[#23556d]`}></div>
          </div>
        )}
      </div>
    );
  };

  // hasIncomingLine=false dipakai bagan SMP: di sana semifinal jadi kolom
  // pertama, jadi ga ada garis masuk dari kiri.
  const SemiFinals = ({ dataSemiFinals, isSMP, overrideRule, hasIncomingLine = true }) => {
    if (overrideRule) {
      return (
        <div className="flex flex-col self-center gap-[22.2rem]">
          <div className="flex">
            <div className="flex flex-col gap-36">
              {dataSemiFinals[0]?.home_club?.id && dataSemiFinals[0]?.away_club?.id ? (
                <TeamCapsule
                  club={dataSemiFinals[0]?.home_club}
                  secondClub={dataSemiFinals[0]?.away_club}
                  hasBack={true}
                  hasFront={true}
                />
              ) : (
                <EmptyTeamCapsule text={"Semifinalis 1"} hasBack={true} hasFront={true} />
              )}
            </div>
          </div>

          <div className="flex">
            <div className="flex flex-col gap-36">
              {dataSemiFinals[0]?.away_club?.id ? (
                <TeamCapsule
                  club={dataSemiFinals[0]?.away_club}
                  secondClub={dataSemiFinals[0]?.away_club}
                  hasBack={true}
                  hasFront={true}
                />
              ) : (
                <EmptyTeamCapsule text={"Semifinalis 2"} hasBack={true} hasFront={true} />
              )}
            </div>
          </div>

          <div className="flex">
            <div className="flex flex-col gap-36">
              {dataSemiFinals[1]?.home_club?.id && dataSemiFinals[1]?.away_club?.id ? (
                <TeamCapsule
                  club={dataSemiFinals[1]?.home_club}
                  secondClub={dataSemiFinals[1]?.away_club}
                  hasBack={true}
                  hasFront={true}
                />
              ) : (
                <EmptyTeamCapsule text={"Semifinalis 3"} hasBack={true} hasFront={true} />
              )}
            </div>
          </div>

          <div className="flex">
            <div className="flex flex-col gap-36">
              {dataSemiFinals[1]?.away_club?.id ? (
                <TeamCapsule
                  club={dataSemiFinals[1]?.away_club}
                  secondClub={dataSemiFinals[1]?.away_club}
                  hasBack={true}
                  hasFront={true}
                />
              ) : (
                <EmptyTeamCapsule text={"Semifinalis 4"} hasBack={true} hasFront={true} />
              )}
            </div>
          </div>
        </div>
      );
    } else if (isSMP) {
      return (
        <div className="flex flex-col self-center gap-36">
          {dataSemiFinals[0] ? (
            <div className="flex">
              <div className="flex flex-col gap-36">
                {dataSemiFinals[0]?.club1 || dataSemiFinals[0]?.home_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[0]?.club1 || dataSemiFinals[0]?.home_club}
                    secondClub={dataSemiFinals[0]?.club2 || dataSemiFinals[0]?.away_club}
                    hasBack={true}
                    // style={{ position: "relative", top: "-80px" }}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 1"} hasBack={true} />
                )}
                {dataSemiFinals[0]?.club2 || dataSemiFinals[0]?.away_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[0]?.club2 || dataSemiFinals[0]?.away_club}
                    secondClub={dataSemiFinals[0]?.club1 || dataSemiFinals[0]?.home_club}
                    hasBack={true}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 2"} hasBack={true} />
                )}
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>

              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          ) : (
            <div className="flex">
              <div className="flex flex-col gap-36">
                <EmptyTeamCapsule text={"Quarter-finalis 1"} hasBack={true} />
                <EmptyTeamCapsule text={"Quarter-finalis 2"} hasBack={true} />
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          )}

          {dataSemiFinals[1] ? (
            <div className="flex">
              <div className="flex flex-col gap-36">
                {dataSemiFinals[1]?.club1 || dataSemiFinals[1]?.home_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[1]?.club1 || dataSemiFinals[1]?.home_club}
                    secondClub={dataSemiFinals[1]?.club2 || dataSemiFinals[1]?.away_club}
                    hasBack={true}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 3"} hasBack={true} />
                )}
                {dataSemiFinals[1]?.club2 || dataSemiFinals[1]?.away_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[1]?.club2 || dataSemiFinals[1]?.away_club}
                    secondClub={dataSemiFinals[1]?.club1 || dataSemiFinals[1]?.home_club}
                    hasBack={true}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 4"} hasBack={true} />
                )}
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          ) : (
            <div className="flex">
              <div className="flex flex-col gap-36">
                <EmptyTeamCapsule text={"Quarter-finalis 3"} hasBack={true} />
                <EmptyTeamCapsule text={"Quarter-finalis 4"} hasBack={true} />
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          )}

          {dataSemiFinals[2] ? (
            <div className="flex">
              <div className="flex flex-col gap-36">
                {dataSemiFinals[2]?.club1 || dataSemiFinals[2]?.home_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[2]?.club1 || dataSemiFinals[2]?.home_club}
                    secondClub={dataSemiFinals[2]?.club2 || dataSemiFinals[2]?.away_club}
                    hasBack={true}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 5"} hasBack={true} />
                )}
                {dataSemiFinals[2]?.club2 || dataSemiFinals[2]?.away_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[2]?.club2 || dataSemiFinals[2]?.away_club}
                    secondClub={dataSemiFinals[2]?.club1 || dataSemiFinals[2]?.home_club}
                    hasBack={true}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 6"} hasBack={true} />
                )}
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          ) : (
            <div className="flex">
              <div className="flex flex-col gap-36">
                <EmptyTeamCapsule text={"Quarter-finalis 5"} hasBack={true} />
                <EmptyTeamCapsule text={"Quarter-finalis 6"} hasBack={true} />
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          )}

          {dataSemiFinals[3] ? (
            <div className="flex">
              <div className="flex flex-col gap-36">
                {dataSemiFinals[3]?.club1 || dataSemiFinals[3]?.home_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[3]?.club1 || dataSemiFinals[3]?.home_club}
                    secondClub={dataSemiFinals[3]?.club2 || dataSemiFinals[3]?.away_club}
                    hasBack={true}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 7"} hasBack={true} />
                )}
                {dataSemiFinals[3]?.club2 || dataSemiFinals[3]?.away_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[3]?.club2 || dataSemiFinals[3]?.away_club}
                    secondClub={dataSemiFinals[3]?.club1 || dataSemiFinals[3]?.home_club}
                    hasBack={true}
                  />
                ) : (
                  <EmptyTeamCapsule text={"Quarter-finalis 8"} hasBack={true} />
                )}
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          ) : (
            <div className="flex">
              <div className="flex flex-col gap-36">
                <EmptyTeamCapsule text={"Quarter-finalis 7"} hasBack={true} />
                <EmptyTeamCapsule text={"Quarter-finalis 8"} hasBack={true} />
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          )}
        </div>
      );
    } else {
      // SMP & Kuliah 2026: kolom pertama = 4 tim (semifinal langsung), labelnya
      // posisi grup (Juara vs Runner-up: 1A/2B, 1B/2A). Untuk SMA kolom ini
      // semifinal beneran — pemenangnya dari babak sebelumnya, jadi generik.
      const emptyLabels = isFourTeamBracket
        ? ["Posisi 1A", "Posisi 2B", "Posisi 1B", "Posisi 2A"]
        : ["Semifinalis 1", "Semifinalis 2", "Semifinalis 3", "Semifinalis 4"];
      return (
        <div className="flex flex-col self-center gap-36">
          {dataSemiFinals[0] ? (
            <div className="flex">
              <div className="flex flex-col gap-36">
                {dataSemiFinals[0]?.club1 || dataSemiFinals[0]?.home_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[0]?.club1 || dataSemiFinals[0]?.home_club}
                    secondClub={dataSemiFinals[0]?.club2 || dataSemiFinals[0]?.away_club}
                    hasBack={hasIncomingLine}
                    // style={{ position: "relative", top: "-80px" }}
                  />
                ) : (
                  <EmptyTeamCapsule text={emptyLabels[0]} hasBack={hasIncomingLine} />
                )}
                {dataSemiFinals[0]?.club2 || dataSemiFinals[0]?.away_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[0]?.club2 || dataSemiFinals[0]?.away_club}
                    secondClub={dataSemiFinals[0]?.club1 || dataSemiFinals[0]?.home_club}
                    hasBack={hasIncomingLine}
                  />
                ) : (
                  <EmptyTeamCapsule text={emptyLabels[1]} hasBack={hasIncomingLine} />
                )}
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          ) : (
            <div className="flex">
              <div className="flex flex-col gap-36">
                <EmptyTeamCapsule text={emptyLabels[0]} hasBack={hasIncomingLine} />
                <EmptyTeamCapsule text={emptyLabels[1]} hasBack={hasIncomingLine} />
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          )}

          {dataSemiFinals[1] ? (
            <div className="flex">
              <div className="flex flex-col gap-36">
                {dataSemiFinals[1]?.club1 || dataSemiFinals[1]?.home_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[1]?.club1 || dataSemiFinals[1]?.home_club}
                    secondClub={dataSemiFinals[1]?.club2 || dataSemiFinals[1]?.away_club}
                    hasBack={hasIncomingLine}
                  />
                ) : (
                  <EmptyTeamCapsule text={emptyLabels[2]} hasBack={hasIncomingLine} />
                )}
                {dataSemiFinals[1]?.club2 || dataSemiFinals[1]?.away_club?.id ? (
                  <TeamCapsule
                    club={dataSemiFinals[1]?.club2 || dataSemiFinals[1]?.away_club}
                    secondClub={dataSemiFinals[1]?.club1 || dataSemiFinals[1]?.home_club}
                    hasBack={hasIncomingLine}
                  />
                ) : (
                  <EmptyTeamCapsule text={emptyLabels[3]} hasBack={hasIncomingLine} />
                )}
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          ) : (
            <div className="flex">
              <div className="flex flex-col gap-36">
                <EmptyTeamCapsule text={emptyLabels[2]} hasBack={hasIncomingLine} />
                <EmptyTeamCapsule text={emptyLabels[3]} hasBack={hasIncomingLine} />
              </div>
              <div className={`relative self-center h-[134px] w-1 bg-[#23556d]`}></div>
              <div className="relative self-center w-[18px] h-[2px] bg-[#23556d]"></div>
            </div>
          )}
        </div>
      );
    }
  };

  const Finals = ({ dataFinals }) => {
    return (
      <div className="border-solid border-2 border-[#34CEF9] p-4 rounded-3xl">
        <h2 className="text-center text-white mb-2">Final</h2>
        <div className="flex flex-col gap-14 ">
          {dataFinals[0] ? (
            <div className="flex flex-col gap-6">
              {dataFinals[0]?.club1 || dataFinals[0]?.home_club?.id ? (
                <TeamCapsule
                  club={dataFinals[0]?.club1 || dataFinals[0]?.home_club}
                  secondClub={dataFinals[0]?.club2 || dataFinals[0]?.away_club}
                  hasFront={false}
                />
              ) : (
                <EmptyTeamCapsule text={"Finalis 1"} hasFront={false} />
              )}
              {dataFinals[0]?.club2 || dataFinals[0]?.away_club?.id ? (
                <TeamCapsule
                  club={dataFinals[0]?.club2 || dataFinals[0]?.away_club}
                  secondClub={dataFinals[0]?.club1 || dataFinals[0]?.home_club}
                  hasFront={false}
                />
              ) : (
                <EmptyTeamCapsule text={"Finalis 2"} hasFront={false} />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"Finalis 1"} hasFront={false} />
              <EmptyTeamCapsule text={"Finalis 2"} hasFront={false} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const ThirdPlace = ({ dataThirdPlace }) => {
    return (
      <div className="border-solid border-2 border-[#34CEF9] p-4 rounded-3xl">
        <h2 className="text-center text-white mb-2">3rd Place</h2>
        <div className="flex flex-col gap-14 ">
          {dataThirdPlace[0] ? (
            <div className="flex flex-col gap-6">
              {dataThirdPlace[0]?.club1 || dataThirdPlace[0]?.home_club ? (
                <TeamCapsule
                  club={dataThirdPlace[0]?.club1 || dataThirdPlace[0]?.home_club}
                  secondClub={dataThirdPlace[0]?.club2 || dataThirdPlace[0]?.away_club}
                  hasFront={false}
                />
              ) : (
                <EmptyTeamCapsule text={"3rd Finalis 1"} hasFront={false} />
              )}
              {dataThirdPlace[0]?.club2 || dataThirdPlace[0]?.away_club ? (
                <TeamCapsule
                  club={dataThirdPlace[0]?.club2 || dataThirdPlace[0]?.away_club}
                  secondClub={dataThirdPlace[0]?.club1 || dataThirdPlace[0]?.home_club}
                  hasFront={false}
                />
              ) : (
                <EmptyTeamCapsule text={"3rd Finalis 2"} hasFront={false} />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <EmptyTeamCapsule text={"3rd Finalis 1"} hasFront={false} />
              <EmptyTeamCapsule text={"3rd Finalis 2"} hasFront={false} />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex items-center overflow-x-auto md:overflow-x-visible md:overflow-y-visible whitespace-nowrap">
      {/* pr-[20rem] dibuang: padding kanan 320px ikut kehitung waktu konten
          di-center, jadi bagannya kedorong ~160px ke kiri dan nyisain jarak
          jauh ke podium juara. */}
      <div className="flex items-start py-7 flex-row rounded-3xl">
        {isSMP ? (
          <>
            <RoundOf16 dataRoundOf16={dataRoundOf16} />
            {/* quarter final */}
            <SemiFinals dataSemiFinals={dataQuarterFinals} isSMP={true} />
            <SemiFinals dataSemiFinals={dataSemiFinals} isSMP={true} overrideRule={true} />
            <div className={`relative self-center h-[795px] w-1 bg-[#23556d]`}>
              <div
                className={`relative self-center h-[2px] w-[105px] bg-[#23556d] top-[397.5px]`}
              ></div>
            </div>

            {/* Gesernya pakai margin, bukan `left-[100px]`: `left` cuma nggeser
                visual, lebar layoutnya tetep, jadi kotak Final-nya nyembul
                keluar panel dan ketimpa podium juara. `relative` tetep dipake
                soalnya kotak 3rd place di dalemnya posisinya absolute. */}
            <div className="flex flex-col gap-6 relative self-center ml-[100px]">
              <div className="flex center">
                <Finals dataFinals={dataFinals} />
              </div>
              <div className="flex center absolute top-[250px] left-[-20px]">
                <div className=" self-center w-[18px] h-[2px] bg-transparent"></div>
                <ThirdPlace dataThirdPlace={dataThirdPlace} />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* SMP & Kuliah cuma 4 tim, jadi ga ada babak quarterfinal sama sekali. */}
            {!isFourTeamBracket && <QuarterFinals dataQuarterFinals={dataQuarterFinals} />}
            <SemiFinals dataSemiFinals={dataSemiFinals} hasIncomingLine={!isFourTeamBracket} />

            <div className={`relative self-center h-[266px] w-1 bg-[#23556d]`}></div>
            <div className="flex flex-col gap-6 relative top-[154px]">
              <div className="flex center">
                <div className="self-center w-[18px] h-[2px] bg-[#23556d]"></div>
                <Finals dataFinals={dataFinals} />
              </div>
              {/* <div className="flex center">
                <div className=" self-center w-[18px] h-[2px] bg-transparent"></div>
                <ThirdPlace dataThirdPlace={dataThirdPlace} />
              </div> */}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KnockoutTable;
